import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader.jsx';
import PageState from '../../components/PageState.jsx';
import SectionCard from '../../components/SectionCard.jsx';
import LifecycleNotice from '../../components/LifecycleNotice.jsx';
import KpiCard, { KpiGrid } from '../../components/KpiCard.jsx';
import { OnguardScoreKpi, SecureScoreKpi } from '../../components/ScoreKpis.jsx';
import { lifecycleNotice } from '../../lib/lifecycle-notice.js';
import { useScoreHistory } from '../../lib/use-score-history.js';
import { trendCaption } from '../../lib/score-history.js';
import { STATUS_WORD, checkCounts, measuresBySeverity } from '../../lib/security-checks.js';
import { customerSectionPath } from '../../lib/navigation.js';
import { SOURCES_ID } from '../data-collection/SourcesCard.jsx';
import MeasureBars from './MeasureBars.jsx';
import { collectionWhen, scoreCaption } from '../overview/OverviewPage.jsx';
import FixFirst from './FixFirst.jsx';
import ByArea from './ByArea.jsx';
import ChecksTable from './ChecksTable.jsx';
import ScoreHistory from './ScoreHistory.jsx';
import { DraftMappingContext } from './parts.jsx';
import CisNotice, { mentionsCis } from '../../components/CisNotice.jsx';
import { exportSecurityReport } from '../../lib/security-export.js';

// The framework ids of every control reference on these checks (evaluates and informs).
const refFrameworks = (findings) => findings.flatMap((f) => ['evaluates', 'informs'].flatMap((k) => (Array.isArray(f?.controlRefs?.[k]) ? f.controlRefs[k].map((r) => r?.framework) : [])));

/**
 * Security, one story with no tabs (ENTRAID-347 redesign, mockup 2, implementation plan C2). Top to bottom:
 *   the page header with "Create report" (plan C2b): a link to Reports that opens the generate dialog on the Security
 *   posture type. Only an account that may generate reports gets it (complianceRole 'admin', `createReportTo`); the
 *   report itself is the server-side Security posture report (pdfmake, pagination gates, retention, auditor access).
 *   An operator account without the admin role gets "Export report" instead, a client-side summary (lib/security-export.js);
 *   4 KPI cards: onGuard score, Microsoft Secure Score, Failed checks, Coverage;
 *   Fix first, By area, All checks (the table, with why a check did not run), Score history.
 * Old deep links keep working: the tab query "findings" lands on All checks, "trends" on Score history, "posture" at the
 * top; the check query (an id) opens that check in the table. Who may open the page is unchanged (lib/navigation.js, operator or the
 * customer's admin). `historyAllowed` follows GET /customers/{id}/runs (operator or customer admin).
 */

export const ALL_CHECKS_ID = 'all-checks';
export const SCORE_HISTORY_ID = 'score-history';
const TAB_TARGET = Object.freeze({ findings: ALL_CHECKS_ID, trends: SCORE_HISTORY_ID });


/** "today's collection at 6:02 AM", "yesterday's collection at 6:02 AM" or "the collection of 20 Oct at 6:02 AM". */
export function collectionPhrase(ts, now = Date.now()) {
  const when = collectionWhen(ts, now);
  if (!when) return null;
  if (when.startsWith('today at ')) return `today's collection at ${when.slice('today at '.length)}`;
  if (when.startsWith('yesterday at ')) return `yesterday's collection at ${when.slice('yesterday at '.length)}`;
  return `the collection of ${when}`;
}

function scrollToId(id) {
  const el = typeof document !== 'undefined' ? document.getElementById(id) : null;
  if (!el) return;
  el.scrollIntoView?.({ block: 'start' });
  el.focus?.({ preventScroll: true });
}

/** The Reports link that opens the generate dialog on the Security posture type (plan C2b). */
function CreateReportLink({ to }) {
  return (
    <Link to={to} className="btn-primary min-h-[40px] gap-2" data-testid="security-create-report">
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" focusable="false">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="12" y1="18" x2="12" y2="12" /><line x1="9" y1="15" x2="15" y2="15" />
      </svg>
      Create report
    </Link>
  );
}

/**
 * The operator account's "Export report" (Hugo Andrade, 2026-10-21): a client-side summary of the posture on this page,
 * because the customer's report function never builds a report for an operator. Display only; nothing is fetched.
 */
function ExportButton({ posture, runTimestamp, companyName }) {
  return (
    <button type="button" className="btn-primary min-h-[40px] gap-2" data-testid="security-export-report" onClick={() => exportSecurityReport(posture, { timestamp: runTimestamp, companyName })} title="Download a summary of this page">
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" focusable="false">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
      </svg>
      Export report
    </button>
  );
}

/**
 * @param {object} props
 * @param {string|null} [props.createReportTo] the Reports path that opens the Security posture dialog; null hides the action
 * @param {boolean} [props.operatorExport] an operator account without the admin role here: "Export report" instead
 */
export default function SecurityPage({ securityPosture, runTimestamp, runs, customer, audience, onRefresh, historyAllowed = false, createReportTo = null, operatorExport = false, readOnly = false }) {
  const posture = securityPosture ?? null;
  if (!posture) return <NoAnalysis runs={runs} customer={customer} audience={audience} onRefresh={onRefresh} readOnly={readOnly} />;
  // readOnly: the viewer role (read-only Overview and Security, 2026-10-25). Every score, KPI, area and check with its
  // details; no report action, no export and no link to Data collection.
  return (
    <DraftMappingContext.Provider value={posture.controlRefsDraft === true}>
      <SecurityStory
        posture={posture} runTimestamp={runTimestamp} runs={runs} customerId={customer?.customerId} historyAllowed={historyAllowed}
        createReportTo={readOnly ? null : createReportTo} operatorExport={readOnly ? false : operatorExport} companyName={customer?.companyName ?? ''}
        readOnly={readOnly}
      />
    </DraftMappingContext.Provider>
  );
}

/** Not deployed, deploying, failed, or no first analysis yet: the lifecycle notice, or an empty state. */
function NoAnalysis({ runs, customer, audience, onRefresh, readOnly = false }) {
  const hasData = (runs?.length ?? 0) > 0;
  const notice = customer ? lifecycleNotice(customer, { audience, hasData }) : null;
  return (
    <div>
      <PageHeader title="Security" subline="onGuard checks and Microsoft Secure Score, from each daily collection." />
      {notice ? (
        <LifecycleNotice customer={customer} audience={audience} hasData={hasData} onRefresh={onRefresh} readOnly={readOnly} />
      ) : (
        <PageState
          state="empty"
          title="No security analysis yet"
          message="The security analysis runs with each data collection. Your score appears after the next scheduled collection."
        />
      )}
    </div>
  );
}

function SecurityStory({ posture, runTimestamp, runs, customerId, historyAllowed, createReportTo, operatorExport, companyName, readOnly = false }) {
  const findings = useMemo(() => (Array.isArray(posture.findings) ? posture.findings : []), [posture]);
  const counts = checkCounts(posture);
  const [params] = useSearchParams();
  const [filterValues, setFilterValues] = useState(() => (counts.fail > 0 ? { status: 'fail' } : {}));
  const [expandedIds, setExpandedIds] = useState([]);
  const pendingScroll = useRef(null);
  const [tick, setTick] = useState(0);

  const { status: historyStatus, history } = useScoreHistory(customerId, { enabled: historyAllowed });
  const showTrend = historyAllowed && historyStatus !== 'forbidden';

  const openCheck = useCallback((id) => {
    const f = findings.find((x) => x.id === id);
    if (!f) return false;
    setFilterValues({ status: f.status });
    setExpandedIds([id]);
    pendingScroll.current = ALL_CHECKS_ID;
    return true;
  }, [findings]);

  const showAllFailing = () => {
    setFilterValues({ status: 'fail' });
    pendingScroll.current = ALL_CHECKS_ID;
    setTick((t) => t + 1);
  };

  // Deep links: the check query opens that check; the old tab query (findings, trends) scrolls to its section.
  const tab = params.get('tab');
  const check = params.get('check');
  useEffect(() => {
    if (check && openCheck(check)) { setTick((t) => t + 1); return; }
    if (TAB_TARGET[tab]) { pendingScroll.current = TAB_TARGET[tab]; setTick((t) => t + 1); }
  }, [tab, check, openCheck]);

  useEffect(() => {
    if (!pendingScroll.current) return;
    const id = pendingScroll.current;
    pendingScroll.current = null;
    scrollToId(id);
  }, [tick]);

  const when = collectionPhrase(runTimestamp);
  const msScore = posture.msSecureScore ?? null;
  const subline = `${counts.total} onGuard ${counts.total === 1 ? 'check' : 'checks'}${msScore ? ' and Microsoft Secure Score' : ''}${when ? `, from ${when}` : ''}.`;
  const filteredStatus = filterValues?.status;

  return (
    <div>
      <PageHeader
        title="Security"
        subline={subline}
        action={createReportTo ? <CreateReportLink to={createReportTo} /> : operatorExport ? <ExportButton posture={posture} runTimestamp={runTimestamp} companyName={companyName} /> : null}
      />
      <div className="space-y-4">
        <SecurityKpis posture={posture} counts={counts} history={history} runs={runs} customerId={customerId} readOnly={readOnly} />
        <FixFirst findings={findings} onShowAllFailing={showAllFailing} onOpenCheck={(id) => { if (openCheck(id)) setTick((t) => t + 1); }} />
        <ByArea categories={posture.categories} msScore={msScore} />
        <div id={ALL_CHECKS_ID} tabIndex={-1} className="scroll-mt-6 focus:outline-none">
          <SectionCard
            title="All checks"
            description={`${counts.total} ${counts.total === 1 ? 'check' : 'checks'}${when ? ` on ${when.replace(/ at .*$/, '')}` : ''}`}
            testId="security-all-checks"
          >
            <ChecksTable
              posture={posture}
              filterValues={filterValues}
              onFilterChange={setFilterValues}
              expandedIds={expandedIds}
              onExpandedChange={setExpandedIds}
            />
            <div className="flex flex-wrap justify-between gap-2 pt-3 text-[13px] text-ipc-muted">
              {filteredStatus ? (
                <span data-testid="all-checks-filter-note">
                  Showing {STATUS_WORD[filteredStatus]?.toLowerCase() ?? filteredStatus} checks.{' '}
                  <button type="button" className="tap font-semibold text-ipc-light hover:underline" onClick={() => setFilterValues({})}>
                    Show all {counts.total}
                  </button>
                </span>
              ) : <span />}
              <span>Guidance and framework references open in each row</span>
            </div>
            {mentionsCis(refFrameworks(findings)) ? <CisNotice className="pt-2 text-xs text-ipc-muted" /> : null}
          </SectionCard>
        </div>
        <ScoreHistory id={SCORE_HISTORY_ID} showTrend={showTrend} history={history} historyStatus={historyStatus} posture={posture} />
      </div>
    </div>
  );
}

function SecurityKpis({ posture, counts, history, runs, customerId, readOnly = false }) {
  const score = posture.score;
  const measures = measuresBySeverity(posture.findings);
  // The totals count onGuard's own checks only (lambda/security-checks.mjs, totalChecks = every onGuard check). The
  // Microsoft Secure Score controls are Microsoft's and are not part of these numbers.
  const needsAccessTo = customerId && !readOnly ? `${customerSectionPath(customerId, 'data-collection')}#${SOURCES_ID}` : null;
  const notRun = `${counts.skip} did not run due to pending permissions or license requirements`;
  return (
    <KpiGrid max={4} data-testid="security-kpis">
      <OnguardScoreKpi score={score} caption={trendCaption(history) ?? scoreCaption(runs?.[0], runs?.[1])} />
      <SecureScoreKpi msScore={posture.msSecureScore} />
      <KpiCard
        label="Overall security measures"
        kpi="Overall security measures"
        ariaLabel={`Overall security measures: ${counts.pass} of ${counts.ran} evaluated onGuard checks passing, ${counts.fail} failing${counts.warn > 0 ? `, ${counts.warn} warnings` : ''}`}
        value={counts.pass}
        unit={`of ${counts.ran} passing`}
        variant="custom"
        visual={<MeasureBars rows={measures} />}
        tone={counts.fail > 0 ? 'bad' : 'good'}
        caption={counts.fail > 0 ? `${counts.fail} failing ${counts.fail === 1 ? 'measure' : 'measures'} to fix` : 'No failing measures'}
      />
      <KpiCard
        label="Running measures"
        kpi="Running measures"
        ariaLabel={`Running measures: ${counts.ran} evaluated of ${counts.total} onGuard checks${counts.skip > 0 ? `, ${notRun}` : ''}`}
        value={counts.ran}
        unit={`of ${counts.total}`}
        variant="tiles"
        filled={counts.ran}
        total={counts.total}
        caption={(
          <span className="block">
            <span className="block text-ipc-ink" data-testid="running-measures-total">{counts.ran} evaluated of {counts.total} total onGuard checks</span>
            {counts.skip > 0 ? (
              needsAccessTo ? (
                <Link to={needsAccessTo} className="font-semibold text-ipc-light hover:underline" data-testid="running-measures-pending">{notRun}</Link>
              ) : <span data-testid="running-measures-pending">{notRun}</span>
            ) : <span>Every check ran</span>}
          </span>
        )}
      />
    </KpiGrid>
  );
}
